import { TemplateOperationExtrasProvider, type TemplateOperationCompleteEvent } from 'adflow';
import { UserContext } from '@adflow/utils';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type FC,
  type PropsWithChildren,
} from 'react';

import { setAuditOutboxUserId } from './auditOutbox';

import { redeemTemplateSavedVersion } from '../savedVersions/fetchTemplateSavedVersions';
import {
  flushUndeliveredAuditsForTemplate,
  postTemplateOperationAudit,
} from './postTemplateOperationAudit';

type Props = PropsWithChildren<{
  templateId?: string;
}>;

const TemplateBuilderOperationAuditProvider: FC<Props> = ({ templateId, children }) => {
  const { user } = useContext(UserContext);
  const userId = user?.getCognitoId?.() ?? '';

  useEffect(() => {
    setAuditOutboxUserId(userId);
  }, [userId]);

  useEffect(() => {
    if (!templateId || !userId) {
      return;
    }
    void flushUndeliveredAuditsForTemplate(templateId);
  }, [templateId, userId]);

  const onOperationComplete = useCallback(
    (event: TemplateOperationCompleteEvent) => {
      const resolvedTemplateId = templateId ?? event.templateId;
      if (!resolvedTemplateId) {
        return;
      }
      void postTemplateOperationAudit(resolvedTemplateId, event).catch((error) => {
        console.warn('[Template operation audit] ingest failed', error);
      });
    },
    [templateId],
  );

  const redeemSavedVersionRestore = useCallback(
    async (args: {
      restoreToken: string;
      expectedWorkingRevision: number;
      expectedPersistedRevision: number;
    }) => {
      if (!templateId) {
        throw new Error('Template id is not available.');
      }
      return redeemTemplateSavedVersion(templateId, args);
    },
    [templateId],
  );

  const value = useMemo(
    () => ({
      templateId,
      onOperationComplete,
      redeemSavedVersionRestore: templateId ? redeemSavedVersionRestore : undefined,
    }),
    [onOperationComplete, redeemSavedVersionRestore, templateId],
  );

  return (
    <TemplateOperationExtrasProvider value={value}>{children}</TemplateOperationExtrasProvider>
  );
};

export default TemplateBuilderOperationAuditProvider;
